/**
 * Import a spec or requirements document (Markdown, text, Word .docx, PDF, HTML), or paste its
 * text. Claude Code reads it on one of your machines and proposes epics, features and stories
 * with acceptance criteria, traced to the document's sections. You review before anything is
 * created; the job runs in the background with its progress on the page.
 */
import { ClipboardPaste, FileText, Upload, X } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { ITEM_KIND_LABEL } from '@orgus/protocol';
import type { BacklogItem, Id, Project } from '@orgus/protocol';
import { AttachmentInput, Button, Dialog, ErrorState, IconButton, Input, Segmented, Select, Textarea, formatBytes, hasMod, toast } from '@orgus/ui';
import type { AttachmentView } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { uploaderFor, useAttachmentsState } from '../attachments/data.ts';
import { startImport } from './data.ts';
import { AiRunOptions, useAiRunOptions } from './AiRunOptions.tsx';

export function ImportDialog({
  open,
  onOpenChange,
  projects,
  items,
  projectId: initialProject,
  onStarted,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  projects: Project[];
  items: readonly BacklogItem[];
  projectId?: Id | null;
  onStarted: (jobId: Id) => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [source, setSource] = useState<'file' | 'paste'>('file');
  const [file, setFile] = useState<AttachmentView | null>(null);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [guidance, setGuidance] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [touched, setTouched] = useState(false);
  const run = useAiRunOptions(open);
  const filesOn = !!useAttachmentsState();
  const upload = useMemo(() => uploaderFor(null), []);

  useEffect(() => {
    if (!open) return;
    setProjectId(initialProject ?? projects[0]?.id ?? '');
    setSource(filesOn ? 'file' : 'paste');
    setFile(null);
    setText('');
    setName('');
    setParentId('');
    setGuidance('');
    setError(null);
    setTouched(false);
    setBusy(false);
    // Only on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parents = items.filter((i) => i.projectId === projectId && (i.kind === 'epic' || i.kind === 'feature') && i.status !== 'dropped');
  const errors = {
    project: projectId ? undefined : projects.length ? 'Choose a project.' : 'No projects are watched yet. Add one in Projects first.',
    source: source === 'file' ? (file ? undefined : 'Choose or drop a document.') : text.trim() ? undefined : 'Paste the document text.',
  };

  const submit = async () => {
    setTouched(true);
    if (errors.project || errors.source || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await startImport({
        projectId,
        ...(source === 'file' ? { attachmentId: file!.id, name: file!.name } : { text, name: name.trim() || 'Pasted text' }),
        ...(guidance.trim() ? { guidance: guidance.trim() } : {}),
        ...(parentId ? { parentId } : {}),
        ...run.value,
      });
      toast.success('Reading the document', `${r.sections} ${r.sections === 1 ? 'section' : 'sections'}. You can keep working; the review opens when it is ready.${r.warnings.length ? ` ${r.warnings[0]}` : ''}`);
      onStarted(r.job.id);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && hasMod(e)) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Import a document"
      description="Claude Code proposes the backlog items; nothing is created until you review them."
      size="lg"
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={submit} shortcut={['mod', 'enter']}>
            Read document
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" onKeyDown={onKeyDown}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select label="Project" value={projectId} onValueChange={(v) => (setProjectId(v), setParentId(''))} options={projects.map((p) => ({ value: p.id, label: p.name }))} placeholder="Choose a project" error={touched ? errors.project : undefined} />
          <Select
            label="Put it under"
            value={parentId}
            onValueChange={setParentId}
            options={[{ value: '', label: 'Nothing: new epics' }, ...parents.map((p) => ({ value: p.id, label: `${p.key} ${ITEM_KIND_LABEL[p.kind]}: ${p.title}` }))]}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Segmented
            className="self-start"
            label="Source"
            value={source}
            onChange={setSource}
            options={[
              { value: 'file', label: 'Upload a file', icon: <Upload />, disabled: !filesOn },
              { value: 'paste', label: 'Paste text', icon: <ClipboardPaste /> },
            ]}
          />
          {source === 'file' ? (
            file ? (
              <div className="flex items-center gap-3 rounded-card border border-border bg-bg px-3 py-2">
                <FileText className="size-5 shrink-0 text-fg-subtle" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-ui text-fg">{file.name}</span>
                <span className="text-caption text-fg-subtle num">{formatBytes(file.size)}</span>
                <IconButton label={`Remove ${file.name}`} icon={<X />} size="xs" onClick={() => setFile(null)} />
              </div>
            ) : (
              <AttachmentInput upload={upload} onUploaded={(a) => setFile(a)} paste={open ? 'document' : 'none'} disabled={busy} hint="Markdown, text, Word .docx, PDF or HTML, up to 25 MB. Headings become the sections requirements trace back to." />
            )
          ) : (
            <>
              <Input label="Name" placeholder="payments-spec.md" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
              <Textarea label="Text" rows={8} placeholder={'# Payments\nCustomers pay by card...'} value={text} onChange={(e) => setText(e.target.value)} className="font-mono" />
            </>
          )}
          {touched && errors.source && <p className="text-ui text-failed">{errors.source}</p>}
        </div>
        <Textarea label="Guidance" hint="Optional." rows={2} value={guidance} onChange={(e) => setGuidance(e.target.value)} maxLength={2000} placeholder="Only the payments part; stories small enough for one day." />
        <AiRunOptions {...run} />
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}
